import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  number,
  shape,
  string,
  bool,
} from 'prop-types';
import { useSelector } from 'react-redux';

import { useWindowSize } from '@hooks';
import { Icon, TabsContainer } from '@components';
import { ReactComponent as KlarnaMarketingBadge } from '@assets/KlarnaMarketingBadge.svg';
import { clearGtmDataLayer } from '@utils/helpers';
import { TRACKING_PRODUCT_TYPES } from '@constants/common';
import { STORE_NAMES } from '@constants';
import { PayWithCardTab, PayWithKlarnaTab } from './tabs';

const PaymentBlock = ({ subscription, isKlarnaDisabled }) => {
  const { t, i18n } = useTranslation();

  const [, isDesktop] = useWindowSize();
  const { authorization_info } = useSelector(store => store[STORE_NAMES.AUTH]);

  const trackGTMBeginCheckout = ({ sub, promotionProps = {} }) => {
    clearGtmDataLayer();
    window.dataLayer.push({
      event: 'begin_checkout',
      eventProps: {
        value: typeof promotionProps.discount === 'number'
          ? (sub.price - promotionProps.discount) / 100
          : sub.price / 100,
        currency: 'NOK',
        coupon: promotionProps.coupon,
        items: [{
          item_id: sub.id,
          item_name: sub.title,
          discount: promotionProps.discount / 100,
          item_category: TRACKING_PRODUCT_TYPES.subscription,
          promotion_id: sub.id,
          promotion_name: promotionProps.promotionName,
        }],
      },
      meta: {
        email: authorization_info.email,
        eventProps: {
          content_category: 'fitness',
          content_ids: [sub.id],
          contents: [{
            id: sub.id,
            quantity: 1,
          }],
          num_items: 1,
          value: sub.price / 100,
          currency: 'NOK',
        },
      },
      userProps: {
        user_id: authorization_info.id,
        subscription_id: sub.id,
      },
    });
  };

  const trackInitiateCheckoutFunction = ({ sub, promotionProps = {} }) => {
    trackGTMBeginCheckout({ sub, promotionProps });
  };

  const tabs = useMemo(() => ([
    {
      title: 'Card',
      id: 1,
      component: (
        <PayWithCardTab
          subscription={subscription}
          trackInitiateCheckout={trackInitiateCheckoutFunction}
        />
      ),
    },
    {
      title: <Icon width={60} SVG={KlarnaMarketingBadge} />,
      id: 2,
      component: (
        <PayWithKlarnaTab
          subscription={subscription}
          disabled={isKlarnaDisabled}
          trackInitiateCheckout={trackInitiateCheckoutFunction}
        />
      ),
      disabled: isKlarnaDisabled,
      tooltipText: t('GetSubscription.Klarna.Tooltip'),
    },
  ]), [
    isDesktop,
    i18n.language,
    subscription,
    isKlarnaDisabled,
  ]);

  return (
    <TabsContainer
      tabs={tabs}
      mainContainerStyles={{
        marginTop: 0,
        padding: 'xl4',
        borderRadius: 'sm',
        height: isDesktop ? '450px' : '100%',
        width: isDesktop ? '50%' : '100%',
      }}
      tabItemsContainerStyles={{ width: '100%', flex: 0 }}
    />
  );
};

PaymentBlock.propTypes = {
  subscription: shape({
    id: number.isRequired,
    price: number.isRequired,
    price_after_first_payment: number.isRequired,
    duration: number.isRequired,
    title: string.isRequired,
    title_no: string.isRequired,
    has_chat: bool.isRequired,
    has_diet_plan: bool.isRequired,
    has_training_program: bool.isRequired,
    has_own_training_program: bool.isRequired,
    has_coach: bool.isRequired,
    has_coaching: bool.isRequired,
    has_booklets: bool.isRequired,
    has_recipes_db: bool.isRequired,
  }),
  isKlarnaDisabled: bool.isRequired,
};

PaymentBlock.defaultProps = {
  subscription: {},
};

export default PaymentBlock;
